// Language on a phone with no signal (docs/LANGUAGE_PLAN.md, L3). Two things live in this
// browser's storage, per person: a copy of the cards as last loaded or changed, so the tab
// opens offline; and the answers given since the last sync, sent in order once back online.
// Answers carry ids made on the phone, so one sent twice is saved once.

import type { Card, CardNote, Review } from '@shared/language/types.ts';
import type { PersonId } from '@shared/types.ts';
import type { LanguageData } from './source.ts';

const COPY = 'natangelo:language:copy:v1:';
const OUTBOX = 'natangelo:language:outbox:v1:';

/** One answer, or the undoing of one that already reached the server. Each carries the card
 *  (and the note, when a leech tag changed) as they should end up. */
export type OutboxOp =
  | { kind: 'answer'; review: Review; card?: Card; note?: CardNote }
  | { kind: 'undo'; reviewId: string; card?: Card; note?: CardNote };

export interface Copy {
  data: LanguageData;
  savedAt: string;
}

function read<T>(storage: Storage | undefined, key: string): T | undefined {
  try {
    const raw = storage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

function write(storage: Storage | undefined, key: string, value: unknown): void {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the tab still works while it's open.
  }
}

export function loadCopy(storage: Storage | undefined, person: PersonId): Copy | undefined {
  const c = read<Copy>(storage, COPY + person);
  return c?.data?.profile && c.savedAt ? c : undefined;
}

export function saveCopy(storage: Storage | undefined, person: PersonId, data: LanguageData, savedAt: string): void {
  write(storage, COPY + person, { data, savedAt } satisfies Copy);
}

export function loadOutbox(storage: Storage | undefined, person: PersonId): OutboxOp[] {
  return read<OutboxOp[]>(storage, OUTBOX + person) ?? [];
}

export function saveOutbox(storage: Storage | undefined, person: PersonId, ops: OutboxOp[]): void {
  try {
    if (ops.length) storage?.setItem(OUTBOX + person, JSON.stringify(ops));
    else storage?.removeItem(OUTBOX + person);
  } catch {
    // as above
  }
}

/** Sign-out forgets the copies (unsent answers stay, and go out at the next sign-in). */
export function clearCopies(storage: Storage | undefined): void {
  try {
    for (const p of ['michael', 'natasha'] as PersonId[]) storage?.removeItem(COPY + p);
  } catch {
    // nothing to clear
  }
}

export const deviceOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/** A failure that means "no connection" rather than "the server said no". */
export function isConnectionError(e: unknown): boolean {
  return deviceOffline() || /failed to fetch|network|load failed|internet/i.test(String((e as Error)?.message ?? e));
}
